import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, User, Menu, X, LogOut, ShieldCheck, Search, ChevronDown } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const LOGO_URL = "/brand/logo.png";

const links = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop", hasCategories: true },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [shopMenuOpen, setShopMenuOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [categories, setCategories] = useState([]);
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    navigate(`/shop?q=${encodeURIComponent(q)}`);
    setSearchOpen(false);
    setMobileOpen(false);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 backdrop-blur-md bg-[#f5ede1]/85 border-b border-[#e5dccd]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 group shrink-0">
          <div className="relative">
            <img
              src={LOGO_URL}
              alt="Snip Happens Boutique"
              className="h-12 sm:h-14 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </div>
          <div className="flex flex-col leading-none">
            <span className="font-display text-base sm:text-xl font-extrabold tracking-tight text-[#1c1815] whitespace-nowrap">
              Snip Happens
            </span>
            <span className="font-script text-base sm:text-lg text-[#e8478e] leading-none mt-0.5">Boutique</span>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-9">
          {links.map((l) => {
            if (l.hasCategories && categories.length > 0) {
              return (
                <div
                  key={l.to}
                  className="relative"
                  onMouseEnter={() => setShopMenuOpen(true)}
                  onMouseLeave={() => setShopMenuOpen(false)}
                >
                  <NavLink
                    to={l.to}
                    data-testid="nav-shop"
                    className={({ isActive }) =>
                      `inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] font-bold transition-colors link-fancy ${
                        isActive ? "text-[#2eb6a8]" : "text-[#1c1815]/80 hover:text-[#e8478e]"
                      }`
                    }
                  >
                    {l.label}
                    <ChevronDown size={13} className={`transition-transform ${shopMenuOpen ? "rotate-180" : ""}`}/>
                  </NavLink>
                  {shopMenuOpen && (
                    <div
                      data-testid="shop-dropdown"
                      className="absolute left-0 top-full pt-3 w-60 z-50"
                    >
                      <div className="bg-white rounded-2xl border border-[#e5dccd] shadow-xl py-2 overflow-hidden">
                        <Link
                          to="/shop"
                          data-testid="shop-dropdown-all"
                          onClick={() => setShopMenuOpen(false)}
                          className="flex items-center justify-between px-5 py-2.5 text-xs uppercase tracking-[0.18em] font-bold text-[#1c1815] hover:bg-[#faf4e9] hover:text-[#e8478e] transition-colors"
                        >
                          All Products
                        </Link>
                        <div className="my-1 border-t border-[#e5dccd]"/>
                        {categories.map((c) => (
                          <Link
                            key={c.id}
                            to={`/shop?category=${c.slug}`}
                            data-testid={`shop-dropdown-${c.slug}`}
                            onClick={() => setShopMenuOpen(false)}
                            className="flex items-center gap-3 px-5 py-2.5 text-sm font-bold text-[#1c1815] hover:bg-[#faf4e9] hover:text-[#e8478e] transition-colors"
                          >
                            <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.accent_color }}/>
                            {c.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            }
            return (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-xs uppercase tracking-[0.2em] font-bold transition-colors link-fancy ${
                    isActive ? "text-[#2eb6a8]" : "text-[#1c1815]/80 hover:text-[#e8478e]"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            );
          })}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Search */}
          <button
            data-testid="header-search-toggle"
            onClick={() => setSearchOpen((v) => !v)}
            className="p-2.5 rounded-full hover:bg-[#efe5d4] transition-colors"
            aria-label="Search products"
          >
            <Search size={19} className="text-[#1c1815]"/>
          </button>

          {user && user.role === "admin" && (
            <Link
              to="/admin"
              data-testid="nav-admin"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-[#e8478e] hover:text-[#c93875]"
            >
              <ShieldCheck size={15}/> Admin
            </Link>
          )}
          {user && user.role !== "admin" && (
            <Link
              to="/account/orders"
              data-testid="nav-orders"
              className="hidden sm:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] font-bold text-[#2eb6a8] hover:text-[#1f8d83]"
            >
              My Orders
            </Link>
          )}

          {user ? (
            <button
              onClick={logout}
              data-testid="nav-logout"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#1c1815]/70 hover:text-[#1c1815]"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          ) : (
            <Link
              to="/login"
              data-testid="nav-login"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-[#1c1815]/80 hover:text-[#2eb6a8]"
            >
              <User size={18} /> Login
            </Link>
          )}

          <button
            data-testid="cart-button"
            onClick={() => setOpen(true)}
            className="relative p-2.5 rounded-full hover:bg-[#efe5d4] transition-colors"
            aria-label="Open cart"
          >
            <ShoppingBag size={20} className="text-[#1c1815]" />
            {count > 0 && (
              <span
                data-testid="cart-count"
                className="absolute -top-0.5 -right-0.5 bg-[#e8478e] text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center shadow-md"
              >
                {count}
              </span>
            )}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-[#1c1815]"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="border-t border-[#e5dccd] bg-[#f5ede1]" data-testid="header-search-panel">
          <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-5 sm:px-8 py-3 flex items-center gap-3">
            <Search size={18} className="text-[#8a8076] shrink-0"/>
            <input
              autoFocus
              data-testid="header-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search shirts, crafts, digital downloads…"
              className="flex-1 bg-transparent text-[#1c1815] placeholder:text-[#8a8076] outline-none text-base"
            />
            <button type="submit" data-testid="header-search-submit"
              className="bg-[#2eb6a8] hover:bg-[#1f8d83] text-white text-[10px] uppercase tracking-[0.2em] font-bold px-5 py-2 rounded-full transition-colors">
              Search
            </button>
            <button type="button" onClick={() => { setSearchOpen(false); setQuery(""); }} className="p-1 hover:bg-[#efe5d4] rounded-full" aria-label="Close search">
              <X size={18} className="text-[#1c1815]"/>
            </button>
          </form>
        </div>
      )}

      {mobileOpen && (
        <div className="md:hidden border-t border-[#e5dccd] bg-[#faf4e9]">
          <div className="px-6 py-4 flex flex-col gap-4">
            {links.map((l) => (
              <div key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                  onClick={() => setMobileOpen(false)}
                  className={({ isActive }) =>
                    `block text-base uppercase tracking-[0.2em] font-bold ${
                      isActive ? "text-[#2eb6a8]" : "text-[#1c1815]"
                    }`
                  }
                  end={l.to === "/"}
                >
                  {l.label}
                </NavLink>
                {l.hasCategories && categories.length > 0 && (
                  <div className="mt-2 ml-3 pl-3 border-l-2 border-[#e5dccd] flex flex-col gap-2">
                    {categories.map((c) => (
                      <Link
                        key={c.id}
                        to={`/shop?category=${c.slug}`}
                        data-testid={`mobile-shop-${c.slug}`}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center gap-2 text-sm font-bold text-[#4a4239] hover:text-[#e8478e]"
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.accent_color }}/>
                        {c.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {user && user.role === "admin" && (
              <NavLink to="/admin" onClick={() => setMobileOpen(false)} className="text-[#e8478e] font-bold uppercase tracking-[0.2em]" data-testid="mobile-nav-admin">
                Admin
              </NavLink>
            )}
            {user && user.role !== "admin" && (
              <NavLink to="/account/orders" onClick={() => setMobileOpen(false)} className="text-[#2eb6a8] font-bold uppercase tracking-[0.2em]" data-testid="mobile-nav-orders">
                My Orders
              </NavLink>
            )}
            {user ? (
              <button onClick={() => { setMobileOpen(false); logout(); navigate("/"); }} className="text-left text-[#1c1815]/70 uppercase tracking-[0.2em] font-bold" data-testid="mobile-nav-logout">
                Logout
              </button>
            ) : (
              <NavLink to="/login" onClick={() => setMobileOpen(false)} className="text-[#1c1815] uppercase tracking-[0.2em] font-bold" data-testid="mobile-nav-login">
                Login
              </NavLink>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
